import React, { useState, forwardRef, useImperativeHandle, ForwardRefRenderFunction } from "react";

import { Drawer, IconButton, Grid } from "@mui/material";
import { FaCheckCircle } from "react-icons/fa";
import { RiCloseLine } from "react-icons/ri";

import BadgeStatus from "Components/Generic/BadgeStatus";
import FieldView from "Components/Form/FieldView";
import useStyles from "./styles";
import { NotificationTemplate } from "Models/notification.template";
import { useTranslation } from "react-i18next";


interface Props {
  data?: NotificationTemplate;
  onClose?: () => void;
}

export interface TemplateViewDrawerHandles {
  openDrawer: () => void;
}

const TemplateViewDrawer: ForwardRefRenderFunction<TemplateViewDrawerHandles, Props> = (
  { data, onClose },
  ref
) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const { t: translate } = useTranslation();

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useImperativeHandle(ref, () => ({
    openDrawer: () => {
      setIsOpen(true);
    },
  }));

  if (!data) return null;

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      PaperProps={{
        className: classes.drawerPaper,
      }}
    >
      <div className={classes.drawerContainer}>
        <nav className={classes.navigationPanel}>
          <h2 className={classes.navigationTitle}>{translate("navigation")}</h2>
          <ul>
            <li className={classes.activeNavItem}>{translate("view")}</li>
          </ul>
        </nav>

        <main className={classes.contentPanel}>
          <header className={classes.header}>
            <h1 className={classes.title}>{translate("defaultTemplate")}</h1>
            <IconButton onClick={handleClose} aria-label="close">
              <RiCloseLine />
            </IconButton>
          </header>

          <div className={classes.scrollableContent}>
            <div className={classes.sectionHeader}>
              <h3 className={classes.sectionTitle}>{translate("details")}</h3>
              <div className={classes.originFile}>
                <FaCheckCircle color="#2E90FA" />
                <span>{translate("originFile")}</span>
              </div>
            </div>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label="Template name" value={data.name} emptyValueInfo="N/A" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label="Subject" value={data.subject} emptyValueInfo="N/A" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span className={classes.detailLabel}>{translate("status")}</span>
                  <BadgeStatus value={data.status} />
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView label="From name" value={data.fromName} emptyValueInfo="N/A" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FieldView
                  label="Available in"
                  value={data.availableIn}
                  emptyValueInfo="N/A"
                  multipleLines={false}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <span className={classes.detailLabel}>{translate("content")}</span>
                  <button className={classes.contentTab}>{translate("email")}</button>
                </div>
              </Grid>
            </Grid>

            <h3 className={classes.sectionTitle}>{translate("templateContent")}</h3>
            <section className={classes.section}>
              <div className={classes.previewWrapper}>
                <div
                  className={classes.templatePreview}
                  dangerouslySetInnerHTML={{ __html: data.html }}
                />
              </div>
            </section>
          </div>
        </main>
      </div>
    </Drawer>
  );
};

export default forwardRef(TemplateViewDrawer);
